import {
  createContext,
  MutableRefObject,
  ReactNode,
  useContext,
  useRef,
  useState
} from 'react';
import type { EditorAppearance } from '@atlaskit/editor-common/types';
import { LOCALSTORAGE_defaultDocData } from '../constants/storage';
import { wacTemplates } from 'src/constants/wac-templates';
import { getContentDataFromQuery } from '../utils/query-data';

interface DocumentData {
  templateId?: string;
  title?: string;
  document: object;
  appearance: EditorAppearance;
}

interface DocumentContext {
  currentTemplateId: MutableRefObject<string | undefined>;
  document: object;
  title: string | undefined;
  appearance: EditorAppearance;
  setDocument: (document: object) => void;
  setTitle: (title: string | undefined) => void;
  setAppearance: (appearance: EditorAppearance) => void;
  save: (value: object) => void;
}

function loadFromLocalStorage(): DocumentData {
  const localDraft =
    localStorage && localStorage.getItem(LOCALSTORAGE_defaultDocData);
  if (localDraft) {
    try {
      return JSON.parse(localDraft);
    } catch (e) {}
  }
  return {
    document: { version: 1, type: 'doc', content: [] },
    appearance: 'full-page'
  };
}

function loadDocumentData(
  loadFromStorage?: boolean,
  loadFromQuery?: boolean
): DocumentData {
  const urlParams = new URLSearchParams(window.location.search);
  const templateId = urlParams.get('templateId');
  if (templateId) {
    const template = Object.values(wacTemplates).find(
      (t) => t.id === templateId
    );
    if (template) {
      return {
        document: template.template,
        appearance: 'full-page'
      };
    }
  }

  if (loadFromQuery) {
    const dataQuery = getContentDataFromQuery();
    if (dataQuery) {
      return {
        document: { version: 1, type: 'doc', content: dataQuery.d },
        title: dataQuery.t,
        appearance: 'full-page'
      };
    }
  }

  return loadFromStorage
    ? loadFromLocalStorage()
    : {
        document: { version: 1, type: 'doc', content: [] },
        appearance: 'full-page'
      };
}

const DocumentStorageContext = createContext<DocumentContext>({
  currentTemplateId: { current: undefined },
  title: undefined,
  document: {},
  appearance: 'full-page',
  setDocument: () => {
    return;
  },
  setTitle: () => {
    return;
  },
  setAppearance: () => {
    return;
  },
  save: () => {
    return;
  }
});

export const useDocumentStorage = () => {
  const context = useContext(DocumentStorageContext);
  if (!context) {
    throw new Error(
      'useDocumentStorage must be used within a DocumentStorageProvider'
    );
  }
  return context;
};

export const DocumentStorageProvider = ({
  children,
  loadFromStorage,
  loadFromQuery
}: {
  children: ReactNode;
  loadFromStorage?: boolean;
  loadFromQuery?: boolean;
}) => {
  const data = loadDocumentData(loadFromStorage, loadFromQuery);
  const currentTemplateId = useRef<string | undefined>(data.templateId);
  const [document, setDocument] = useState<object>(data.document);
  const [title, setTitle] = useState<string | undefined>(data.title);
  const [appearance, setAppearance] = useState<EditorAppearance>(
    data.appearance
  );

  const save = (value: object) => {
    localStorage.setItem(
      LOCALSTORAGE_defaultDocData,
      JSON.stringify({ title, document: value, appearance })
    );
  };

  return (
    <DocumentStorageContext.Provider
      value={{
        currentTemplateId,
        document,
        title,
        appearance,
        setDocument,
        setTitle,
        setAppearance,
        save
      }}
    >
      {children}
    </DocumentStorageContext.Provider>
  );
};
